import { useEffect } from "react";

export interface PageMeta {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "瓜田速递";
const ORIGIN = "https://cllcr.cn";

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * 为当前页面设置独立的 title / description / keywords / canonical / OG / Twitter
 * 以及可选的 Schema.org JSON-LD 结构化数据。
 */
export default function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const fullTitle = meta.title.includes(SITE_NAME) ? meta.title : `${meta.title} - ${SITE_NAME}`;
    const url = `${ORIGIN}${meta.path}`;
    const type = meta.type ?? "website";

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', "name", "description", meta.description);
    upsertMeta('meta[name="keywords"]', "name", "keywords", meta.keywords);
    upsertMeta('meta[name="robots"]', "name", "robots", "index, follow");

    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", meta.description);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", "zh_CN");

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", meta.description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    const scriptId = "page-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (meta.jsonLd) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
    }
  }, [meta]);
}